import { api } from "./apiSlice";
import { showMessage } from "../../helpers";

export const partnerInvoiceApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getPartnerInvoiceData: builder.query({
      query: (params = {}) => {
        const { limit, offset, ordering, ...otherParams } = params;

        let url = "/partner/royalty-invoice-tracking/";
        const queryParams = new URLSearchParams();

        // Add pagination parameters
        if (limit !== undefined) {
          queryParams.append("limit", limit);
        }
        if (offset !== undefined) {
          queryParams.append("offset", offset);
        }

        // Add ordering parameter
        if (ordering) {
          queryParams.append("ordering", ordering);
        }

        // Handle array parameters (filters that use getlist on backend)
        const arrayParams = ["year", "quarter", "partner", "licensor_name", "status"];

        Object.entries(otherParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (arrayParams.includes(key) && Array.isArray(value)) {
              value.forEach((item) => {
                if (item !== undefined && item !== null && item !== "") {
                  queryParams.append(key, item);
                }
              });
            } else if (!arrayParams.includes(key)) {
              queryParams.append(key, value);
            }
          }
        });

        // Append query parameters to URL if any exist
        const queryString = queryParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }

        return url;
      },
      providesTags: ["PartnerInvoice"],
    }),

    getPartnerTaxInvoiceData: builder.query({
      query: (params = {}) => {
        const { limit, offset, ordering, ...otherParams } = params;

        let url = "/partner/royalty-tax-invoice/";
        const queryParams = new URLSearchParams();

        if (limit !== undefined) {
          queryParams.append("limit", limit);
        }
        if (offset !== undefined) {
          queryParams.append("offset", offset);
        }

        if (ordering) {
          queryParams.append("ordering", ordering);
        }

        const arrayParams = ["year", "quarter", "partner", "licensor_name", "status"];

        Object.entries(otherParams).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            if (arrayParams.includes(key) && Array.isArray(value)) {
              value.forEach((item) => {
                if (item !== undefined && item !== null && item !== "") {
                  queryParams.append(key, item);
                }
              });
            } else if (!arrayParams.includes(key)) {
              queryParams.append(key, value);
            }
          }
        });

        const queryString = queryParams.toString();
        if (queryString) {
          url += `?${queryString}`;
        }

        return url;
      },
      providesTags: ["PartnerInvoice"],
    }),
    
    //NEW: Get individual partner tax invoice by ID
    getIndividualPartnerTaxInvoiceData: builder.query({
      query: (id) => ({
        url: `/partner/royalty-tax-invoice/${id}/`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [
        { type: "PartnerInvoice", id },
      ],
    }),

    downloadInvoiceDocument: builder.mutation({
      query: ({ fileName, requestObj }) => ({
        url: "/invoice/royalty/download/",
        method: "POST",
        body: requestObj,
        responseHandler: async (response) => {
          return await response.blob();
        },
      }),
      async onQueryStarted({ fileName }, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          const blob = new Blob([data], { type: "text/csv" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.parentNode.removeChild(link);
        } catch (error) {
          showMessage("error", "Failed to download document");
        }
      },
    }),

    // Invoice filters
    getPartnerInvoiceFilters: builder.query({
      query: () => "/partner/royalty-invoice-tracking/filters",
      providesTags: ["PartnerInvoiceFilters"],
    }),

    // Tax invoice filters
    getPartnerTaxInvoiceFilters: builder.query({
      query: () => "/partner/royalty-tax-invoice/filters",
      providesTags: ["PartnerTaxInvoiceFilters"],
    }),

    sendInvoiceReminder: builder.mutation({
      query: (tracking_id) => ({
        url: "/invoice-approval-reminder/",
        method: "POST",
        body: { tracking_id },
      }),
      invalidatesTags: ["PartnerInvoice"],
      async onQueryStarted(tracking_id, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Reminder sent successfully");
        } catch (error) {
          showMessage("error", "Failed to send reminder");
        }
      },
    }),

    // Invoice operations
    getSingleInvoiceRequest: builder.query({
      query: (id) => ({
        url: `/partner/royalty-invoice-tracking/${id}/`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "PartnerInvoice", id }],
    }),

    addInvoiceRequest: builder.mutation({
      query: (data) => ({
        url: "/partner/royalty-invoice-tracking/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PartnerInvoice"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Invoice Request raised successfully!");
        } catch (error) {
          showMessage("error", "Failed to submit invoice request");
        }
      },
    }),

    editInvoiceRequest: builder.mutation({
      query: ({ id, data }) => ({
        url: `/partner/royalty-invoice-tracking/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        "PartnerInvoice",
        { type: "PartnerInvoice", id },
      ],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Request edited successfully!");
        } catch (error) {
          showMessage("error", "Failed to update invoice request");
        }
      },
    }),

    deleteInvoiceFileRequest: builder.mutation({
      query: (id) => ({
        url: `/partner/royalty-invoice-tracking/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["PartnerInvoice"],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          showMessage("success", "Removed!");
        } catch (error) {
          showMessage("error", "Failed to delete");
        }
      },
    }),
  }),
});

export const {
  useGetPartnerInvoiceDataQuery,
  useGetPartnerTaxInvoiceDataQuery,
  useGetIndividualPartnerTaxInvoiceDataQuery,
  useGetPartnerInvoiceFiltersQuery,
  useGetPartnerTaxInvoiceFiltersQuery,
  useDownloadInvoiceDocumentMutation,
  useSendInvoiceReminderMutation,
  useGetSingleInvoiceRequestQuery,
  useAddInvoiceRequestMutation,
  useEditInvoiceRequestMutation,
  useDeleteInvoiceFileRequestMutation,
} = partnerInvoiceApi;
